import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { getFontFaceStyleBlock, injectFontFaceIntoSvg } from "./embedFont";

// Render an SVG markup string to a transparent PNG at the requested size.
// Returns a data URL (image/png).
//
// We embed the Mukta Mahee @font-face block inside the SVG before rasterization
// — without it the rendered text falls back to the OS sans-serif because
// blob-URL-loaded SVGs don't inherit the page's @font-face rules.
export async function svgToPngDataUrl(
    svgMarkup: string,
    pxWidth: number,
    pxHeight: number,
): Promise<string> {
    const fontBlock = await getFontFaceStyleBlock();
    const withFont = injectFontFaceIntoSvg(svgMarkup, fontBlock);
    const svgBlob = new Blob([withFont], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = pxWidth;
                canvas.height = pxHeight;
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    reject(new Error("Could not get 2d context"));
                    return;
                }
                ctx.clearRect(0, 0, pxWidth, pxHeight);
                ctx.drawImage(img, 0, 0, pxWidth, pxHeight);
                resolve(canvas.toDataURL("image/png"));
            };
            img.onerror = () => reject(new Error("SVG image load failed"));
            img.src = url;
        });
        return dataUrl;
    } finally {
        URL.revokeObjectURL(url);
    }
}

// Persist a data URL PNG to the cache directory and return a webview-accessible URL
// suitable for handing to the Instagram share plugin. Falls back to the data URL
// on web where Filesystem isn't usable.
export async function persistPngForShare(
    dataUrl: string,
    fileName: string,
): Promise<string> {
    if (!Capacitor.isNativePlatform()) return dataUrl;

    const base64 = dataUrl.split(",")[1];
    if (!base64) throw new Error("Invalid PNG data URL");

    const written = await Filesystem.writeFile({
        path: `ecliptic-stickers/${fileName}`,
        data: base64,
        directory: Directory.Cache,
        recursive: true,
    });

    // Native IG share plugin reads from disk — hand it the raw file:// URI,
    // not the WebView-internal capacitor:// URL.
    return written.uri;
}

// Serialize a live <svg> DOM node to a string (preserves computed attrs).
export function serializeSvgElement(svg: SVGSVGElement): string {
    return new XMLSerializer().serializeToString(svg);
}
